import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell,
} from "recharts";
import { Briefcase, Users, Sparkles, Zap, Clock, TrendingUp } from "lucide-react";

const STATUS_LABELS = {
  submitted: "Submitted",
  shortlisted: "Shortlisted",
  interviewed: "Interviewed",
  hired: "Hired",
  rejected: "Rejected",
};

const STATUS_COLORS = {
  submitted: "#3B82F6",
  shortlisted: "#06B6D4",
  interviewed: "#F59E0B",
  hired: "#10B981",
  rejected: "#EF4444",
};

const RANGES = [
  { key: "7d", label: "7d" },
  { key: "30d", label: "30d" },
  { key: "90d", label: "90d" },
  { key: "all", label: "All time" },
];

export const RecruiterAnalytics = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("30d");

  useEffect(() => {
    setLoading(true);
    api.get(`/stats/recruiter?range=${range}`)
      .then((r) => setStats(r.data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, [range]);

  if (loading) {
    return (
      <div className="mt-8 border border-white/10 bg-[#111827] rounded-xl p-6 text-slate-500 text-sm">
        Loading analytics…
      </div>
    );
  }
  if (!stats) return null;

  const funnelData = Object.entries(stats.funnel).map(([key, value]) => ({
    stage: STATUS_LABELS[key] || key,
    key,
    count: value,
  }));

  const byJobData = (stats.by_job || []).map((j) => ({
    ...j,
    title: j.title?.length > 22 ? j.title.slice(0, 20) + "…" : j.title,
  }));

  return (
    <div className="mt-8" data-testid="recruiter-analytics">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="mono-label">analytics</div>
        <div className="inline-flex rounded-full border border-white/10 bg-[#111827] p-1" data-testid="analytics-range-group">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => setRange(r.key)}
              data-testid={`analytics-range-${r.key}`}
              className={`text-xs px-3 py-1 rounded-full font-mono uppercase transition-colors ${
                range === r.key
                  ? "bg-[#06B6D4] text-[#0B0F19]"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <MetricCard label="Active jobs" value={stats.totals.jobs} icon={Briefcase} />
        <MetricCard label="Total applicants" value={stats.totals.applicants} icon={Users} />
        <MetricCard label="Avg. match" value={`${stats.totals.avg_match}`} suffix="/100" icon={Sparkles} accent="#06B6D4" />
        <MetricCard
          label="High-match rate"
          value={`${stats.totals.high_match_pct}`}
          suffix="%"
          icon={Zap}
          accent="#10B981"
        />
      </div>

      <div className="grid lg:grid-cols-5 gap-4 mt-4">
        {/* Funnel */}
        <div className="lg:col-span-3 border border-white/10 bg-[#111827] rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="mono-label">applicant funnel</div>
              <div className="text-sm text-slate-400 mt-1">Across all your open roles</div>
            </div>
            <TrendingUp className="w-4 h-4 text-slate-500" />
          </div>
          <div className="h-56" data-testid="recruiter-funnel-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={funnelData} margin={{ top: 8, right: 8, bottom: 4, left: -20 }}>
                <XAxis
                  dataKey="stage"
                  tick={{ fill: "#94A3B8", fontSize: 11, fontFamily: "JetBrains Mono, monospace" }}
                  axisLine={{ stroke: "rgba(255,255,255,0.08)" }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: "#64748B", fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  cursor={{ fill: "rgba(255,255,255,0.03)" }}
                  contentStyle={{
                    background: "#0B0F19",
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: 8,
                    fontSize: 12,
                    color: "#E2E8F0",
                  }}
                />
                <Bar dataKey="count" radius={[6, 6, 0, 0]} maxBarSize={54}>
                  {funnelData.map((d) => (
                    <Cell key={d.key} fill={STATUS_COLORS[d.key] || "#3B82F6"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Time to shortlist */}
        <div className="lg:col-span-2 border border-white/10 bg-[#111827] rounded-xl p-5 flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <div className="mono-label">time to shortlist</div>
            <Clock className="w-4 h-4 text-slate-500" />
          </div>
          <div className="flex-1 flex items-center">
            <div>
              <div className="font-display text-5xl font-semibold">
                {stats.time_to_shortlist_days ?? "—"}
                <span className="text-slate-500 text-xl ml-1">
                  {stats.time_to_shortlist_days != null ? "days" : ""}
                </span>
              </div>
              <div className="text-xs text-slate-500 mt-2 leading-relaxed">
                {stats.time_to_shortlist_days != null
                  ? "Average across your shortlisted applicants."
                  : "No applicants shortlisted yet. Move a candidate to \"Shortlisted\" to start measuring."}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Per-job breakdown */}
      {byJobData.length > 0 && (
        <div className="mt-4 border border-white/10 bg-[#111827] rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="mono-label">top roles by applicants</div>
              <div className="text-sm text-slate-400 mt-1">Volume × quality (avg. match) per role</div>
            </div>
          </div>
          <div className="h-56" data-testid="recruiter-per-job-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byJobData} margin={{ top: 8, right: 8, bottom: 4, left: -20 }}>
                <XAxis
                  dataKey="title"
                  tick={{ fill: "#94A3B8", fontSize: 10 }}
                  axisLine={{ stroke: "rgba(255,255,255,0.08)" }}
                  tickLine={false}
                  interval={0}
                  angle={-14}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  tick={{ fill: "#64748B", fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  cursor={{ fill: "rgba(255,255,255,0.03)" }}
                  contentStyle={{
                    background: "#0B0F19",
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: 8,
                    fontSize: 12,
                    color: "#E2E8F0",
                  }}
                />
                <Bar dataKey="applicants" fill="#3B82F6" radius={[6, 6, 0, 0]} maxBarSize={40} name="Applicants" />
                <Bar dataKey="avg_match" fill="#06B6D4" radius={[6, 6, 0, 0]} maxBarSize={40} name="Avg. match" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

const MetricCard = ({ label, value, suffix, icon: Icon, accent }) => (
  <div className="border border-white/10 bg-[#111827] rounded-xl p-5">
    <div className="flex items-center justify-between">
      <div className="mono-label">{label}</div>
      <Icon className="w-4 h-4 text-slate-500" />
    </div>
    <div className="font-display text-3xl mt-3" style={{ color: accent || "#F8FAFC" }}>
      {value}
      {suffix && <span className="text-slate-500 text-base ml-1">{suffix}</span>}
    </div>
  </div>
);
